import { FC, ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart as RechartsPieChart, ResponsiveContainer, Sector } from 'recharts';
import { COMMON_TRANSLATION } from '@constants/index';
import { colors } from '@modules/study-insights/constants';
import { textToTitleCase } from '@modules/study-insights/utils';

import { ActiveShapeProps, CustomLabelProps, PieChartProps, ShapeValues, ShapeValuesProps } from './PieChart.types';
import { StyledLabelContainer, StyledNoData } from './PieChart.styles';
import { ChartGroupTotals } from '@common/types';

const PieChart: FC<PieChartProps> = ({ chartColors, data, showLabel = true }) => {
  const { t } = useTranslation([COMMON_TRANSLATION]);
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  const onPieEnter = useCallback(
    (_: any, index: number) => {
      setActiveIndex(index);
    },
    [setActiveIndex],
  );

  const onPieLeave = useCallback(() => {
    setActiveIndex(undefined);
  }, [setActiveIndex]);

  const getShapeValues = (props: ShapeValuesProps): ShapeValues => {
    const { cx, cy, midAngle, outerRadius } = props;
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(RADIAN * midAngle);
    const cos = Math.cos(RADIAN * midAngle);
    const mx = cx + (outerRadius + 10) * cos;
    const ex = mx + (cos >= 0 ? 1 : -1) * 20;
    const sx = cx + outerRadius * cos;
    const sy = cy + outerRadius * -sin;
    const my = cy + (outerRadius + 10 * Math.abs(sin)) * -sin;
    const ey = my;

    return { cos, ex, ey, mx, my, sx, sy };
  };

  const renderCustomLabel = (props: CustomLabelProps): ReactElement => {
    const { cx, cy, midAngle, outerRadius, index, textAnchor } = props;
    const percentage: number = data[index].percentage;
    const shapeValues = { cx, cy, midAngle, outerRadius };
    const { cos, ex, ey, mx, my, sx, sy } = getShapeValues(shapeValues);

    return (
      <g>
        {activeIndex !== index && percentage > 5 && (
          <>
            <text
              x={ex + (cos >= 0 ? 1 : -1) * 12}
              y={ey}
              textAnchor={textAnchor}
              fontSize="12px"
              fill={colors.darkGray300}
            >
              {`${percentage.toFixed(1)}%, ${data[index].total}`}
            </text>
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={colors.darkGray700} fill="none" />
          </>
        )}
      </g>
    );
  };

  const renderActiveShape = (props: ActiveShapeProps): ReactElement => {
    const { cx, cy, endAngle, fill, midAngle, outerRadius, payload, startAngle, textAnchor } = props;
    const item = payload as unknown as ChartGroupTotals;
    const shapeValues = {
      cx,
      cy,
      midAngle,
      outerRadius: outerRadius + 6,
    };

    const { cos, ex, ey, mx, my, sx, sy } = getShapeValues(shapeValues);

    const positionX = ex + (cos >= 0 ? 1 : -9) * 12;

    return (
      <g>
        <Sector
          filter="drop-shadow(0px 4px 10px rgb(0 0 0 / 0.4))"
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          outerRadius={outerRadius + 6}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={colors.darkGray700} strokeWidth="3px" fill="none" />

        <foreignObject x={positionX} y={ey} width="150" height="100">
          <StyledLabelContainer css={{ color: colors.darkBlue400 }}>{textToTitleCase(item.name)}</StyledLabelContainer>
        </foreignObject>

        <text
          x={positionX}
          y={ey - 5}
          textAnchor={textAnchor}
          fontSize="12px"
          fontWeight="620"
          fill={colors.darkGray100}
        >
          {`${item.percentage.toFixed(1)}%, ${item.total}`}
        </text>
      </g>
    );
  };

  if (!data?.length) {
    return <StyledNoData>{t('no_data_available', { ns: COMMON_TRANSLATION })}</StyledNoData>;
  }

  return (
    <ResponsiveContainer aspect={1} maxHeight={300} width={'100%'}>
      <RechartsPieChart margin={{ left: 80, right: 80 }}>
        <Pie
          activeIndex={activeIndex}
          activeShape={renderActiveShape}
          data={data}
          outerRadius={100}
          dataKey="total"
          stroke="none"
          onMouseEnter={onPieEnter}
          onMouseLeave={onPieLeave}
          label={showLabel && renderCustomLabel}
          labelLine={false}
        >
          {data.map((_entry: any, index: number) => (
            <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
          ))}
        </Pie>
      </RechartsPieChart>
    </ResponsiveContainer>
  );
};
export default PieChart;
